import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';

import { Observable, first, of, switchMap, take } from 'rxjs';
import { GlobalPopupService } from '../../components/generic-popup/presets/global-popup/global-popup.service';

export interface HasChanges {
   hasChanges: boolean;
   /** targetUrl: where the user was heading, for components that navigate there themselves after an async save. */
   saveUnsavedChanges(targetUrl?: string): Observable<boolean>;
   checkHasChanges(): void;
   isSavingChanges: boolean;
}

export const hasChangesGuard = (
   component: HasChanges,
   route: ActivatedRouteSnapshot,
   state: RouterStateSnapshot,
   nextState?: RouterStateSnapshot
): Observable<boolean> => {
   const location = inject(Location);
   const router = inject(Router);
   const globalPopupService = inject(GlobalPopupService);

   // ✅ wrap dialog handling in a new observable
   return new Observable<boolean>((observer) => {

      if(component.isSavingChanges){
         // If already saving changes, allow navigation
         observer.next(true);
         observer.complete();
         component.isSavingChanges = false; // reset saving state
         return;
      }

      // DevExtreme editors default to valueChangeEvent="change", which only commits
      // [(value)] into the component's model on blur/Enter - not on every keystroke. A
      // field the user just typed into and left focused (e.g. clicked the browser Back
      // button, which doesn't reliably blur the page's active element first) can still
      // be holding its old value here, making checkHasChanges() compare against stale
      // data and silently report "no changes". Forcing a blur first flushes any pending
      // edit synchronously before the comparison runs.
      (document.activeElement as HTMLElement | null)?.blur?.();

      component.checkHasChanges();

      if(!component.hasChanges){
         observer.next(true); // allow navigation
         observer.complete();
         return;
      }

      // Dismissing without a button (outside click, Escape, close icon) must behave
      // like Cancel, or the guard's observable never completes and navigation hangs.
      const cancel = () => {
         observer.next(false); // cancel navigation
         observer.complete();
      };

      globalPopupService.showPopup({
         title: $localize`Unsaved Changes`,
         content: $localize`You have unsaved changes. Do you want to save them before leaving?`,
         width: 620, // wide enough for all 3 footer buttons to stay on one line
         onClose: cancel,
         // Discard sits in its own left-aligned group, spaced apart from Cancel/Save
         // on the right, so the destructive action isn't right next to the primary one.
         leftButtons: [
            {
               text: $localize`Discard Changes`,
               type: 'danger-outline',
               onClick: () => {
                  observer.next(true); // allow navigation
                  observer.complete();
               }
            }
         ],
         buttons: [
            {
               text: $localize`Cancel`,
               type: 'secondary',
               onClick: cancel
            },
            {
               text: $localize`Save and Continue`,
               type: 'primary',
               onClick: () => {
                  component.saveUnsavedChanges(nextState?.url).subscribe((success) => {
                     observer.next(success); // true or false
                     observer.complete();
                  });
               }
            }
         ]
      });
   });
};